import type { CapitalGlobalCoverageCatalogItem } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  findBenefitOption,
  isAuxilioCoverage,
  usesBenefitSelectionWithPercent,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  capCapitalAndPercent,
  getCoverageCapitalsByCode,
  getEffectiveLimits,
  getPercentReferenceCapital,
  hasCatalogMax,
} from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import type { CoverageState } from '../sections/coverages-table.component';

export type DerivedCoverageRow = {
  enabled: boolean;
  capital: number;
  percent: string;
};

function parsePercentToNumber(raw: string): number {
  return parseFloat(String(raw).replace(',', '.'));
}

function parseCurrencyToNumber(raw: string): number {
  return parseInt(String(raw).replace(/\D/g, ''), 10) / 100;
}

/**
 * Teto em R$ para replicar auxílio entre grupos escalonados.
 * Usa só o `limitMax` do catálogo — sem `Morte × proportion`, que varia por
 * grupo e cortaria a verba absoluta no grupo de capital menor.
 */
function getAuxilioAbsoluteMax(item: CapitalGlobalCoverageCatalogItem): number {
  if (!hasCatalogMax(item)) {
    return Number.POSITIVE_INFINITY;
  }
  return item.limitMax as number;
}

/**
 * Deriva os valores de cobertura de um grupo escalonado a partir do input único
 * (percentuais do grupo de maior capital).
 *
 * Morte: capital = (% / 100) × capitalDoGrupo.
 * Demais: capital = (% / 100) × MorteDoGrupo (mesmo % relativo à Morte do grupo).
 * Auxílio: mesma verba em R$ informada no formulário para todos os grupos
 * (teto = limitMax do catálogo; % recalculado por grupo).
 */
export function deriveGroupCoverages(params: {
  rows: Record<string, CoverageState>;
  morteId: string | undefined;
  groupReferenceCapital: number;
  largestReferenceCapital: number;
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
}): Record<string, DerivedCoverageRow> {
  const { rows, morteId, groupReferenceCapital, coverageCatalog } = params;

  const result: Record<string, DerivedCoverageRow> = {};

  let morteCapitalForGroup = 0;
  if (morteId && groupReferenceCapital > 0) {
    const morteRow = rows[morteId];
    const mortePct = parsePercentToNumber(morteRow?.percent ?? '');
    if (morteRow?.enabled && Number.isFinite(mortePct) && mortePct > 0) {
      morteCapitalForGroup = (groupReferenceCapital * mortePct) / 100;
    }
  }

  const limitsCtx = {
    referenceCapital: groupReferenceCapital,
    morteCapital: morteCapitalForGroup,
    morteId,
    coverageCapitalsByCode: getCoverageCapitalsByCode(coverageCatalog, rows),
  };

  const morteItem = coverageCatalog.find(item => item.id === morteId);
  if (morteItem && morteCapitalForGroup > 0) {
    limitsCtx.coverageCapitalsByCode.set(morteItem.code, morteCapitalForGroup);
  }

  for (const item of coverageCatalog) {
    const row = rows[item.id];
    if (!row?.enabled) continue;

    /*
     * Cobertura contratada por benefício: a opção escolhida vale igual para
     * todos os grupos — nada de escalonar por %. Opção sem valor em R$ entra
     * com capital 0 e mesmo assim precisa sobreviver ao derive, senão a linha
     * some do attach do grupo (é o benefício que carrega a contratação).
     * Exceção: nas diárias (WITH_SI) o % é o input do valor da diária, então a
     * linha ESCALONA como as demais — cai no caminho do % logo abaixo.
     */
    const benefitOption = findBenefitOption(item, row.benefitCode);
    if (benefitOption && !usesBenefitSelectionWithPercent(item)) {
      const absoluteCapital = benefitOption.capital ?? 0;
      if (absoluteCapital <= 0) {
        result[item.id] = { enabled: true, capital: 0, percent: '' };
        continue;
      }

      const percentRef = getPercentReferenceCapital(item, limitsCtx);
      const { capital, percent } = capCapitalAndPercent({
        capital: absoluteCapital,
        percentRefCapital: percentRef > 0 ? percentRef : 1,
        effectiveMax: getAuxilioAbsoluteMax(item),
        effectiveMin: 0,
      });

      result[item.id] = { enabled: true, capital, percent };
      limitsCtx.coverageCapitalsByCode.set(item.code, capital);
      continue;
    }

    if (isAuxilioCoverage(item)) {
      const absoluteCapital = parseCurrencyToNumber(row.maxIndividualCapital);
      if (absoluteCapital <= 0) continue;

      const percentRef = getPercentReferenceCapital(item, limitsCtx);
      const { capital, percent } = capCapitalAndPercent({
        capital: absoluteCapital,
        percentRefCapital: percentRef > 0 ? percentRef : 1,
        effectiveMax: getAuxilioAbsoluteMax(item),
        effectiveMin: 0,
      });

      result[item.id] = {
        enabled: true,
        capital,
        percent,
      };
      limitsCtx.coverageCapitalsByCode.set(item.code, capital);
      continue;
    }

    const pct = parsePercentToNumber(row.percent);
    if (!Number.isFinite(pct) || pct <= 0) {
      // Diárias com benefício escolhido e % ainda vazio (ex.: cotação salva
      // antes da regra do %): a linha sobrevive com capital 0 para não sumir
      // do attach do grupo — é o benefício que carrega a contratação.
      if (benefitOption) {
        result[item.id] = { enabled: true, capital: 0, percent: '' };
      }
      continue;
    }

    const percentRef = getPercentReferenceCapital(item, limitsCtx);
    const rawCapital = percentRef > 0 ? (percentRef * pct) / 100 : 0;

    const limits = getEffectiveLimits(item, limitsCtx);
    const { capital } = capCapitalAndPercent({
      capital: rawCapital,
      percentRefCapital: percentRef,
      effectiveMax: limits.max,
      effectiveMin: 0,
    });

    result[item.id] = {
      enabled: true,
      capital,
      percent: row.percent,
    };
    limitsCtx.coverageCapitalsByCode.set(item.code, capital);
  }

  return result;
}
